import React, { useState } from 'react';
import { 
  privacyPolicyFull, 
  healthDataConsent, 
  telemedicineConsentFull, 
  termsOfService 
} from '../data/legalDocuments';
import { legalDisclaimer } from '../data/mock';
import { FileText, Shield, Video, ScrollText, AlertCircle, ArrowLeft } from 'lucide-react';

const LegalPage = () => {
  const [activeDocument, setActiveDocument] = useState('privacy');

  const documents = [
    {
      id: 'privacy',
      title: 'Privacy Policy',
      icon: Shield,
      data: privacyPolicyFull
    },
    {
      id: 'health',
      title: 'Health Data Consent',
      icon: FileText,
      data: healthDataConsent
    },
    {
      id: 'telemedicine',
      title: 'Telemedicine Consent',
      icon: Video,
      data: telemedicineConsentFull
    },
    {
      id: 'terms',
      title: 'Terms of Service',
      icon: ScrollText,
      data: termsOfService
    },
    {
      id: 'disclaimer',
      title: 'Medical Disclaimer',
      icon: AlertCircle,
      data: {
        title: legalDisclaimer.title,
        lastUpdated: 'January 2025',
        content: legalDisclaimer.content
      }
    }
  ];

  const activeDoc = documents.find(d => d.id === activeDocument);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0f172a] text-white py-6">
        <div className="container">
          <a href="/" className="inline-flex items-center gap-2 text-sm text-[#94a3b8] hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} />
            Back to Home
          </a>
          <h1 className="text-2xl font-medium">Legal Documents</h1>
          <p className="text-[#94a3b8] mt-2">Dr. Carlos Castellon, M.D. — General & Preventative Medicine</p>
        </div>
      </header>

      <div className="container py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="sticky top-8">
              <p className="text-xs uppercase tracking-wider text-[#64748b] mb-4 font-medium">Documents</p>
              <ul className="space-y-2">
                {documents.map((doc) => (
                  <li key={doc.id}>
                    <button
                      onClick={() => setActiveDocument(doc.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                        activeDocument === doc.id
                          ? 'bg-[#0f172a] text-white'
                          : 'text-[#475569] hover:bg-[#f8fafc]'
                      }`}
                    >
                      <doc.icon size={18} />
                      {doc.title}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-4 bg-[#f8fafc] border border-[#e2e8f0]">
                <p className="text-xs text-[#64748b] leading-relaxed">
                  These documents govern your use of our medical services. By booking an appointment, you agree to these terms.
                </p>
              </div>
            </nav>
          </div>

          {/* Document Content */}
          <div className="lg:col-span-3">
            <div className="border border-[#e2e8f0] p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-2">
                <activeDoc.icon size={24} className="text-[#b8860b]" />
                <h2 className="text-xl font-medium text-[#0f172a]">{activeDoc.data.title}</h2>
              </div>
              <p className="text-sm text-[#64748b] mb-8">Last Updated: {activeDoc.data.lastUpdated}</p>
              
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-line text-[#475569] leading-relaxed text-sm">
                  {activeDoc.data.content}
                </div>
              </div>
            </div>

            {/* Print/Download Note */}
            <div className="mt-6 flex items-center justify-between text-sm text-[#64748b]">
              <p>For questions about these documents, please contact us.</p>
              <button 
                onClick={() => window.print()} 
                className="text-[#0f172a] hover:text-[#b8860b] transition-colors"
              >
                Print this document
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#f8fafc] border-t border-[#e2e8f0] py-8">
        <div className="container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-[#64748b]">
              © {new Date().getFullYear()} Dr. Carlos Castellon, M.D. All rights reserved.
            </p>
            <p className="text-sm text-[#64748b]">
              Medical License No. 072870415 — Ibiza, Spain
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
